import { memo, useCallback, useContext, useEffect, useRef, type MouseEvent } from 'react';
import styled from '@emotion/styled';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatTextInputControls } from './hooks/useChatTextInputControls';
import { TextHintIcon, TextHintWrap } from '../presentation/Message.QueryHint';

type LearnMoreProps = {
  displayValue?: string;
  open: boolean;
  question: string;
};

export const LearnMore = memo(({ open, question, displayValue = 'Learn More' }: LearnMoreProps) => {
  const dispatch = useContext(ChatStateDispatchContext);
  const { focusChatInputTextArea } = useChatTextInputControls();

  const textRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({
      type: 'input-changed',
      payload: { value: question },
    });
    focusChatInputTextArea();
  }, [dispatch, focusChatInputTextArea, question]);

  useEffect(() => {
    const textEl = textRef.current;

    const handleTransitionEnd = () => {
      textEl.style.opacity = '1';
    };

    const handleTransitionStart = () => {
      textRef.current.style.opacity = '0';
    };

    if (textEl) {
      textEl.addEventListener('transitionstart', handleTransitionStart);
      textEl.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      if (textEl) {
        textEl.removeEventListener('transitionstart', handleTransitionStart);
        textEl.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, []);

  return (
    <LearnMoreRoot onClick={handleClick}>
      <StyledLearnMoreWrap>
        <StyledTextHintIcon /> <LearnMoreText open={open} ref={textRef}>{open ? displayValue : ''}</LearnMoreText>
      </StyledLearnMoreWrap>
    </LearnMoreRoot>
  );
});

const LearnMoreRoot = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 5,
  width: 95,
});

const StyledLearnMoreWrap = styled(TextHintWrap)({
  fontSize: 13,
  padding: `0 1px 0 3px`,
  alignItems: 'center',
  height: 22,
  borderRadius: 5,
});

const LearnMoreText = styled.span<{ open: boolean }>(({ open }) => ({
  marginLeft: 3,
  opacity: 0,
  width: open ? 70 : 0,
  transition: 'width .1s ease-in-out',
  overflowX: 'hidden',
}));

const StyledTextHintIcon = styled(TextHintIcon)({
  marginRight: 0,
});
